<script setup lang="ts">
import { computed, type PropType, SetupContext, useAttrs } from "vue";
import { ButtonType, ControlSize, DesignVersion } from "@/models/types";

defineOptions({ name: "BcButton", inheritAttrs: false });

const attrs: SetupContext["attrs"] = useAttrs();

const props = defineProps({
  /** Specifies the button's style */
  design: {
    type: String as PropType<DesignVersion>,
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables button for click */
  disabled: { type: Boolean, default: false },
  /** Expands the button to the width of its parent container */
  fullWidth: { type: Boolean, default: false },
  /** Expands the button to the height of its parent container */
  fullHeight: { type: Boolean, default: false },
  /** Removes all background styles */
  outlined: { type: Boolean, default: false },
  /** Removes all background and border styles */
  plain: { type: Boolean, default: false },
  /** Makes the button's border rounded */
  rounded: { type: Boolean, default: false },
  /** Specifies pre-defined sizes for the button */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
  /** Specifies the type of button */
  type: {
    type: String as PropType<ButtonType>,
    default: "button",
    validator: (value: any) => ButtonType.isButtonType(value),
  },
});

const computedTag = computed<string>(() => {
  if (attrs.href !== undefined && attrs.href !== null && typeof attrs.href === "string") {
    return "a";
  }
  if (
    attrs.to !== undefined &&
    attrs.to !== null &&
    (typeof attrs.to === "string" || typeof attrs.to === "object")
  ) {
    return "router-link";
  }
  return "button";
});

const pak = computed<string | undefined>(() => {
  return props.design ? "bc-controls-d" + props.design : undefined;
});

defineExpose({
  computedTag,
});
</script>

<template>
  <component
    :is="computedTag"
    :data-pak="pak"
    :data-design="design"
    class="bissantzControls buttonControl"
    v-bind="$attrs"
    :disabled="disabled"
    :type="computedTag === 'button' ? type : undefined"
    :class="[
      size ? `button-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        fullHeight: fullHeight,
        outlined: outlined,
        plain: plain,
        rounded: rounded,
      },
    ]"
  >
    <slot />
  </component>
</template>

<style lang="less">
@import "../styles/components/bc-button.less";
</style>
